define([
    'angular',
    'underscore',
    'text!./templates/message-template.html',
    'text!edge/platform/widgets/modal/templates/progress-template.html'
], (angular, _, messageTemplate, progressTemplate)=> {

    const CSS_NAMESPACE = 'namespace_qnb';

    let counter = 0;

    const _cleanClose = (modalPromise, cssClasses) => {
        counter++;

        //workaround for the angular modal issue: https://github.com/dwmkerr/angular-modal-service/issues/41
        modalPromise.result.finally(() => {
            counter--;
            if (counter === 0) {
                const htmlBody = angular.element('body');
                htmlBody.removeClass(cssClasses);
            }
        });
    };

    const _showMessage = ($filter, $modal, title, message, description, actionObj, iconClass, templateStr, showCancel, options, cancelBtnLabel)=> {
        const opts = {
            template: templateStr,
            backdrop: true,
            windowClass: CSS_NAMESPACE,
            openedClass: CSS_NAMESPACE,
            controller: 'ModalController',
            resolve: {
                model: ()=> {
                    return {
                        title,
                        message,
                        description,
                        showCancelBtn: showCancel,
                        keyboard: 'false',
                        iconClass,
                        cancelBtnLabel: cancelBtnLabel || $filter('translate')('platform.modal.ModalService.Close'),
                        action: actionObj
                    };
                }
            }
        };
        if (options) {
            angular.extend(opts, options);
        }

        const modalPromise = $modal.open(opts);
        _cleanClose(modalPromise, opts.windowClass);

        return modalPromise;
    };

    return ['$modal', '$filter', '$q', ($modal, $filter, $q)=> {
        return {
            showInfo: (title, message, description, actionLabel, actionBtnClass)=> {
                const actionProperties = {
                    label: actionLabel || $filter('translate')('platform.modal.ModalService.Ok'),
                    btnClass: actionBtnClass || 'gw-btn-primary',
                    backdrop: 'static'
                };

                return _showMessage($filter, $modal, title, message, description, actionProperties, 'gw-messageInformationIcon', messageTemplate);
            },

            showConfirm: (title, message, description, actionLabel, actionBtnClass, cancelLabel)=> {
                const actionProperties = {
                    label: actionLabel,
                    btnClass: actionBtnClass || 'gw-btn-primary'
                };
                cancelLabel = cancelLabel || $filter('translate')('platform.modal.ModalService.Cancel');
                const opts = {
                    keyboard: false
                };
                return _showMessage($filter, $modal, title, message, description, actionProperties, 'gw-messageQuestionIcon', messageTemplate, true, opts, cancelLabel);
            },

            showWarning: (title, message, description, actionLabel, actionBtnClass, cancelLabel)=> {
                const actionProperties = {
                    label: actionLabel || $filter('translate')('platform.modal.ModalService.Yes'),
                    btnClass: actionBtnClass || 'gw-btn-primary'
                };
                const opts = {
                    keyboard: false
                };
                cancelLabel = cancelLabel || $filter('translate')('platform.modal.ModalService.Cancel');
                return _showMessage($filter, $modal, title, message, description, actionProperties, 'gw-messageWarningIcon', messageTemplate, true, opts, cancelLabel);
            },

            showError: (title, message, description, actionLabel, actionBtnClass)=> {
                const actionProperties = {
                    label: actionLabel || $filter('translate')('platform.modal.ModalService.Close'),
                    btnClass: actionBtnClass
                };
                return _showMessage($filter, $modal, title, message, description, actionProperties, 'gw-messageErrorIcon', messageTemplate);
            },

            showSuccess: (title, message, description, actionLabel, actionBtnClass)=> {
                const actionProperties = {
                    label: actionLabel || $filter('translate')('platform.modal.ModalService.Ok'),
                    btnClass: actionBtnClass || 'gw-btn-primary'
                };
                return _showMessage($filter, $modal, title, message, description, actionProperties, 'gw-messageSuccessIcon', messageTemplate);
            },

            showProgressDialog: (title, hidePromise)=> {
                const msgBox = $modal.open({
                    template: progressTemplate,
                    keyboard: false,
                    backdrop: 'static',
                    windowClass: CSS_NAMESPACE + ' modal-no-background',
                    openedClass: CSS_NAMESPACE,
                    controller: 'ModalController',
                    resolve: {
                        model: ()=> {
                            return {
                                title
                            };
                        }
                    }
                });

                msgBox.opened.then(() => {
                    $q.when(hidePromise, response => {
                        msgBox.close(response);
                        return response;
                    }, error => {
                        msgBox.dismiss(error);
                        return error;
                    });
                });

                _cleanClose(msgBox, CSS_NAMESPACE + ' modal-no-background');

                return msgBox;
            },

            showTwoPromiseProgressDialog: (title, hidePromise1, hidePromise2)=> {
                const msgBox = $modal.open({
                    template: progressTemplate,
                    keyboard: false,
                    backdrop: 'static',
                    windowClass: CSS_NAMESPACE,
                    openedClass: CSS_NAMESPACE,
                    controller: 'ModalController',
                    resolve: {
                        model: ()=> {
                            return {
                                title
                            };
                        }
                    }
                });

                let prom1Returned = false;
                let prom2Returned = false;

                $q.when(hidePromise1, ()=> {
                    prom1Returned = true;
                    if (prom2Returned === true) {
                        msgBox.close();
                    }
                }, (error)=> {
                    console.error(error);
                    msgBox.close();
                    return $q.reject([error]);
                });

                $q.when(hidePromise2, ()=> {
                    prom2Returned = true;
                    if (prom1Returned === true) {
                        msgBox.close();
                    }
                }, (error)=> {
                    console.error(error);
                    msgBox.close();
                    return $q.reject([error]);
                });

                _cleanClose(msgBox, CSS_NAMESPACE);
            },

            /**
             * Shows a simple dialog composed of external template and controller components. It allows user to create
             * new dialog using just a simple functions and not a separate "controller" instance (which is anyway
             * just a same collection of functions). Not recommended for a complex scenarios (use separate controller
             * instead).
             *
             * @param template data rendering template.
             * @param data simple scope data. This map is copied to scope "as-is". Useful to provide initial (or data)
             * model values, some basic (non-scope) functions.
             * @param scopeFunctions functions to bind to a scope. This map should have only a functions as it's values.
             * Each that function should receive a $scope argument as it's first value. This functions are bound
             * (partially applied) to a context. Result of that partial application is bound to the scope using
             * original function's key.
             * <p> Example. If we have a pair 'someFunction' : function($scope, a, b, c) {}, resulting scope will have
             * a function 'someFunction': function(a, b, c) {};
             * @param initializer optional function to invoke on the scope. This function may install watches,
             * binding, etc...
             */
            showSimpleDialog(template, data, scopeFunctions, initializer) {
                const msgBox = $modal.open({
                    template,
                    backdrop: true,
                    windowClass: CSS_NAMESPACE,
                    openedClass: CSS_NAMESPACE,
                    controller: ['$scope', ($scope)=> {
                        let key;
                        if (data) {
                            for (key in data) {
                                if (data.hasOwnProperty(key)) {
                                    $scope[key] = data[key];
                                }
                            }
                        }

                        if (scopeFunctions) {
                            for (key in scopeFunctions) {
                                if (scopeFunctions.hasOwnProperty(key)) {
                                    $scope[key] = _.partial(scopeFunctions[key], $scope);
                                }
                            }
                        }

                        if (initializer) {
                            initializer($scope);
                        }
                    }]
                });

                _cleanClose(msgBox, CSS_NAMESPACE);
            }

        };
    }];
});



// WEBPACK FOOTER //
// ../common/js/src/edge/platform/widgets/modal/ModalService.js